import { FC, useContext, useEffect, useState, useRef } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification, showSuccessNotification } from "../../../../../context/notificationContext";
import { createLoadOptions } from "../../../../../hooks/useDebounce";
import useI18n from "../../../../../hooks/useTranslations";
import { useItemsApi } from "../../../../../http/useItems";
import { itemLabel } from "../../../../../utils";
import { HrailWaterproofKit } from "../../../../../http/hrail/types/waterproofKits";
import { useHrailSystemsApi } from "../../../../../http/hrail/useSystems";
import { useHrailWaterproofKitsApi } from "../../../../../http/hrail/useWaterproofKits";
import Button from "../../../../ui/Button";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import MultiCheckbox from "../../../../ui/MultiCheckbox";
import Numeric from "../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { CustomAsyncSelect } from "../../../../ui/select/CustomSelect";

interface WaterproofKitFormBody {
  item: SelectOption | null;
  systemIds: string[];
  order: number | null;
  visibility: VisibilityScopeValue;
}

type SelectOption = {
  id: string;
  label: string;
};

interface WaterproofKitFormProps {
  waterproofKit?: HrailWaterproofKit;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  onCreated?: (id: string) => void;
  onSaved?: () => void;
}

const WaterproofKitForm: FC<WaterproofKitFormProps> = ({ waterproofKit, isDisabled, isLoading, onClose, onCreated, onSaved }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const waterproofKitApi = useHrailWaterproofKitsApi();
  const systemApi = useHrailSystemsApi();
  const itemsApi = useItemsApi();
  const [itemOptions, setItemOptions] = useState<SelectOption[]>([]);
  const itemAborter = useRef(new AbortController());
  const systemsAborter = useRef(new AbortController());
  const itemSearchTerm = useRef("");
  const loadItemOptions = createLoadOptions(itemAborter, itemSearchTerm, (query, signal) =>
    itemsApi.getAdminItems(query, signal).then((items) => items.map(toItemOption)),
  );

  const defaultValues: WaterproofKitFormBody = {
    item: waterproofKit?.item ? toItemOption(waterproofKit.item) : null,
    systemIds: waterproofKit?.systems.map((system) => system.id) ?? [],
    order: waterproofKit?.order ?? 50,
    visibility: waterproofKit?.visibility ?? "internal",
  };

  useEffect(() => {
    loadItemOptions("").then(setItemOptions).catch(() => setItemOptions([]));
  }, []);

  const systems = systemApi.useAdminHrailSystems({ limit: 100 }, systemsAborter.current.signal);

  useEffect(() => {
    return () => {
      itemAborter.current.abort();
      systemsAborter.current.abort();
    };
  }, []);

  const onSubmit = () => async (formData: WaterproofKitFormBody) => {
    setIsSubmitting(true);

    try {
      if (waterproofKit) {
        await waterproofKitApi.updateAdminHrailWaterproofKit(waterproofKit.item.id, {
          systemIds: formData.systemIds,
          order: formData.order ?? 50,
          visibility: formData.visibility,
        });
        dispatch(showSuccessNotification(i18n.translation));
        onSaved?.();
        onClose?.();
      } else {
        const res = await waterproofKitApi.insertAdminHrailWaterproofKit({
          itemId: formData.item?.id ?? "",
          systemIds: formData.systemIds,
          order: formData.order ?? 50,
          visibility: formData.visibility,
        });
        dispatch(showSuccessNotification(i18n.translation));
        onCreated?.(res.id);
        onSaved?.();
        onClose?.();
      }
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form<WaterproofKitFormBody>
      defaultValues={defaultValues}
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      secondaryButton={
        onClose ? (
          <Button buttonProps={{ onClick: onClose }} type="secondary">
            {i18n.translation.common.cancel}
          </Button>
        ) : undefined
      }
      onSubmit={onSubmit}
      fullWidth
    >
      {({ control, reset, setValue, watch }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [waterproofKit]);

        const systemIds = watch("systemIds");

        return (
          <FormFieldsContainer fullWidth>
            <Controller
              name="item"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.items.singular} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <CustomAsyncSelect<SelectOption>
                      {...field}
                      inputId={labelId}
                      defaultOptions={itemOptions}
                      loadOptions={loadItemOptions}
                      isClearable={false}
                      isDisabled={isDisabled || isLoading || isSubmitting || !!waterproofKit}
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.id}
                      isInvalid={isInvalid}
                      onChange={(value) => setValue("item", value ?? null)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField label={i18n.translation.systems.plural}>
              <MultiCheckbox
                disabled={isDisabled || isLoading || isSubmitting}
                values={
                  systems.data?.items.map((system) => ({
                    id: system.id,
                    title: system.name,
                    selected: systemIds.includes(system.id),
                  })) ?? []
                }
                onUpdate={(id, checked) =>
                  setValue("systemIds", checked ? [...systemIds, id] : systemIds.filter((systemId) => systemId !== id))
                }
              />
            </FormField>
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error} isOptional={false}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={typeof field.value === "number" ? field.value : null}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      min={0}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

type ItemOptionSource = { id: string; code?: string | null; description?: string | null; name?: string | null };

const toItemOption = (item: ItemOptionSource): SelectOption => ({ id: item.id, label: itemLabel(item) });

export default WaterproofKitForm;
